import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client/react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@scio3/ui';
import { DELETE_COMPANY } from '@scio3/graphql-client';
import { signOut, auth } from '@/lib/firebase';
import { useAuthStore } from '@/stores/auth-store';
import { usePermissions } from '@/hooks/usePermissions';
import { useDialogSubmitState } from '@/hooks/useDialogSubmitState';
import { Scio1Frame } from '@/components/iframe/Scio1Frame';

interface PaymentData {
  isTrial?: boolean;
  isActive?: boolean;
}

export function isOptiSignsDomain(): boolean {
  const host = window.location.hostname;
  return (
    host === 'localhost' ||
    host === 'optisigns.com' ||
    host.endsWith('.optisigns.com') ||
    host.endsWith('.opstech.org')
  );
}

// ---------------------------------------------------------------------------
// DeleteAccountDialog — separate confirmation dialog matching scio1 UX
// ---------------------------------------------------------------------------

function DeleteAccountDialog({
  open,
  onOpenChange,
  onConfirm,
  deleting,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  deleting: boolean;
}) {
  const { t } = useTranslation();
  const [confirmText, setConfirmText] = useState('');
  const isConfirmed = confirmText === 'DELETE';
  const { disabled, disabledTooltip } = useDialogSubmitState({
    isSubmitting: deleting,
    isConfirmed,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="delete-account-dialog">
        <DialogHeader>
          <DialogTitle>{t('Delete Account')}</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-4">
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                {t('deleteAccountModal1_prefix')}{' '}
                <span className="font-semibold text-destructive">
                  {t('This action cannot be undone.')}
                </span>{' '}
                {t('Do you wish to continue?')}
              </div>
              <div className="space-y-2">
                <Input
                  data-testid="delete-account-confirm-input"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="DELETE"
                />
                <p className="text-sm text-muted-foreground">
                  {t('Please type "DELETE" and click OK to continue.')}
                </p>
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">{t('Cancel')}</Button>
          </DialogClose>
          <Button
            variant="destructive"
            data-testid="delete-account-ok-btn"
            onClick={onConfirm}
            disabled={disabled}
            disabledTooltip={disabledTooltip}
          >
            {t('OK')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// InactiveAccountDialog — main blocking dialog
// ---------------------------------------------------------------------------

export function InactiveAccountDialog() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const me = useAuthStore((s) => s.me);
  const { isRootOwner } = usePermissions();
  const [deleteCompany] = useMutation(DELETE_COMPANY);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  // When true, hides the native dialog and shows the full-screen scio1 payment iframe.
  // scio1 handles the rest (its own inactive modal + payment flow).
  // Recovery: scio1 sends IS_CLOSE_SUBSCRIPTION_MODAL → ME_QUERY refetch → isAccountInactive flips.
  const [showScio1Payment, setShowScio1Payment] = useState(false);

  const payment = me?.payment as PaymentData | undefined;
  const isTrial = payment?.isTrial === true;
  const isWhiteLabel = !!me?.brandingData?.isWhiteLabel;
  const showUpdate = isOptiSignsDomain() && !isWhiteLabel;
  const showDelete = isRootOwner && !isWhiteLabel;

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch {
      // Even if signOut fails, navigate away — auth state clears on next load
    }
    navigate('/signIn');
  };

  const handleUpdate = () => {
    setShowScio1Payment(true);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteCompany();
      await signOut(auth);
      navigate('/signIn');
    } catch {
      toast.error(t('Failed to delete account. Please try again or contact support.'));
    } finally {
      setDeleting(false);
    }
  };

  // After clicking Update: hand off to scio1's full-screen payment page.
  // scio1 will show its own inactive modal with payment options.
  if (showScio1Payment) {
    return (
      <Scio1Frame
        targetState={isTrial ? 'app.account.subscriptionPlan' : 'app.adapterPortalSecond'}
        className="h-screen w-full border-0"
      />
    );
  }

  return (
    <>
      <AlertDialog open>
        <AlertDialogContent
          data-testid="inactive-account-dialog"
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>{t('Attention')}</AlertDialogTitle>
            <AlertDialogDescription>
              {isTrial
                ? t('freePlanDowngradeInactive')
                : t(
                    'Your subscription needs attention. Please update your payment information to continue.',
                  )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:flex-row sm:justify-between">
            {showDelete && (
              <Button
                variant="destructive"
                data-testid="inactive-delete-account-btn"
                onClick={() => setShowDeleteDialog(true)}
              >
                {t('Delete Account')}
              </Button>
            )}
            <div className="flex gap-2 sm:ml-auto">
              <Button variant="outline" data-testid="inactive-logout-btn" onClick={handleLogout}>
                {t('Logout')}
              </Button>
              {showUpdate && (
                <Button data-testid="inactive-update-btn" onClick={handleUpdate}>
                  {isTrial ? t('Update Subscription') : t('Update')}
                </Button>
              )}
            </div>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {showDelete && (
        <DeleteAccountDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          onConfirm={handleDelete}
          deleting={deleting}
        />
      )}
    </>
  );
}
